import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const MonitoramentoTempos: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getTemposEspera(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );

        setData(result);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados de tempos de espera.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar tempos de espera.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const porUnidade = data?.porUnidade || [];
  const tendencia = data?.tendencia || [];

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Tempos de Espera
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* Por Unidade - Grouped BarChart */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Tempo Medio por Unidade (minutos)
        </Typography>
        {porUnidade.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(porUnidade.length * 50, 300)}>
            <BarChart
              data={porUnidade}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={(v: number) => `${v.toLocaleString('pt-BR')} min`}
              />
              <YAxis
                type="category"
                dataKey="nome_unidade"
                width={200}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: any, name: any) => {
                  const label = String(name) === 'avg_acolhimento_min'
                    ? 'Acolhimento'
                    : 'Recepcao ate Alta';
                  return [`${Number(value).toLocaleString('pt-BR')} min`, label];
                }}
              />
              <Legend
                formatter={(value: any) => {
                  if (value === 'avg_acolhimento_min') return 'Acolhimento';
                  if (value === 'avg_recepcao_alta_min') return 'Recepcao ate Alta';
                  return value;
                }}
              />
              <Bar
                dataKey="avg_acolhimento_min"
                fill="#007AF0"
                radius={[0, 4, 4, 0]}
                name="avg_acolhimento_min"
              />
              <Bar
                dataKey="avg_recepcao_alta_min"
                fill="#E76F00"
                radius={[0, 4, 4, 0]}
                name="avg_recepcao_alta_min"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de tempos por unidade para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Tendencia - LineChart com duas linhas */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Evolucao dos Tempos de Espera
        </Typography>
        {tendencia.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={tendencia} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="data" tick={{ fontSize: 12 }} />
              <YAxis
                tickFormatter={(v: number) => `${v.toLocaleString('pt-BR')} min`}
              />
              <Tooltip
                formatter={(value: any, name: any) => {
                  const label = String(name) === 'avg_acolhimento'
                    ? 'Acolhimento'
                    : 'Recepcao ate Alta';
                  return [`${Number(value).toLocaleString('pt-BR')} min`, label];
                }}
              />
              <Legend
                formatter={(value: any) => {
                  if (value === 'avg_acolhimento') return 'Acolhimento';
                  if (value === 'avg_recepcao_alta') return 'Recepcao ate Alta';
                  return value;
                }}
              />
              <Line
                type="monotone"
                dataKey="avg_acolhimento"
                stroke="#007AF0"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
                name="avg_acolhimento"
              />
              <Line
                type="monotone"
                dataKey="avg_recepcao_alta"
                stroke="#E76F00"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
                name="avg_recepcao_alta"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de tendencia para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="tempos-espera"
          contexto="dashboard"
          manualTrigger
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoTempos;
